import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18next from "i18next";

import logo from "../../assets/icons/numuw_v2_logo_icon.svg";
import globe_icon from "../../assets/icons/globe_icon.svg";
import { useLocal } from "hooks/useLocal";
import { routes } from "constants/index";

// Underlines the nav link for the page the visitor is on.
const activeLinkClassName = "border-b-2 border-[#4242CE] pb-0.5";

const Header = ({ consultButton, onPrimaryCtaClick }) => {
  const { t, i18n } = useTranslation(["common"]);
  const { isArabic } = useLocal();
  const [searchParams, setSearchParams] = useSearchParams();
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const lang = searchParams.get("lang") || localStorage.getItem("i18nextLng");
    if (lang) i18next.changeLanguage(lang);
    setSearchParams({ lang: lang || "en" });
  }, []);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    setSearchParams({ lang });
  };

  const currentLang = localStorage.getItem("i18nextLng") || "en";

  const otherLang = currentLang === "en" ? "ar" : "en";
  const otherLangLabel = otherLang === "en" ? "English" : "العربية";

  const handlePrimaryCtaClick = () => {
    setMenuOpen(false);
    if (onPrimaryCtaClick) onPrimaryCtaClick();
  };

  const lan = isArabic ? "ar" : "en";
  const lanWithParam = isArabic ? "?lang=ar" : "";

  // School pages link back to the parents site; every other page links to schools.
  const isSchoolPage = pathname.startsWith(routes.school);
  const audienceLink = isSchoolPage
    ? { label: t("header.forParents"), to: "/" + lanWithParam, show: true }
    : { label: t("header.forSchools"), to: routes.school + lanWithParam, show: true };

  const primaryNavLinks = [
    {
      label: t("buttons.joinNetwork"),
      to: "/join-our-network" + lanWithParam,
      show: consultButton,
    },
    // { label: t("buttons.courses"), to: "/courses" + lanWithParam, show: true },
    audienceLink,
    {
      label: "Resources Hub",
      href: "https://resources.numuw.com/",
      external: true,
      show: true,
    },
  ].filter((l) => l.show);
  const secondaryLinks = [
    { label: "Contact Us", href: "#" },
    {
      label: "Help Center",
      href: "https://intercom.help/numuw-telehealth/" + lan,
    },
    { label: "Privacy Policy", href: "/privacy-policy" + lanWithParam },
    { label: "Terms of Service", href: "/terms-of-use" + lanWithParam },
  ];

  const desktopNavLinks = [
    {
      label: t("buttons.joinNetwork"),
      to: "/join-our-network" + lanWithParam,
      show: consultButton,
    },
    // { label: t("buttons.courses"), to: "/courses" + lanWithParam, show: true },
    audienceLink,
    {
      label: t("resources"),
      href: `https://resources.numuw.com/${isArabic ? "ar" : ""}`,
      external: true,
      show: true,
    },
  ].filter((l) => l.show);

  return (
    <>
      <nav
        className={`w-full bg-white border-b border-solid border-[#EBEBEB] px-6 py-4 ${isArabic ? "font-ibmplex" : "font-inter"} relative z-50`}
      >
        <div className="flex items-center justify-between max-w-[1400px] mx-auto">
          {/* Logo */}
          <Link to="/" onClick={() => setMenuOpen(false)}>
            <img src={logo} alt="logo" className="w-[50px] md:w-[78px]" />
          </Link>

          {/* Desktop Nav */}
          <ul className="hidden md:flex items-center gap-6">
            {desktopNavLinks.map((link) => (
              <li key={link.label}>
                {link.external ? (
                  <a
                    href={link.href}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-[#4242CE] hover:text-indigo-600 whitespace-nowrap"
                  >
                    {link.label}
                  </a>
                ) : (
                  <NavLink
                    to={link.to}
                    className={({ isActive }) =>
                      `text-sm text-[#4242CE] hover:text-indigo-600 whitespace-nowrap ${isActive ? activeLinkClassName : ""}`
                    }
                  >
                    {link.label}
                  </NavLink>
                )}
              </li>
            ))}
          </ul>

          {/* Desktop Right */}
          <div className="hidden md:flex items-center gap-6 relative">
            {/* Language */}
            <div>
              <button
                onClick={() => handleLanguageChange(otherLang)}
                className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-indigo-600 font-medium"
                aria-label={`Switch language to ${otherLang === "en" ? "English" : "العربية"}`}
              >
                <img src={globe_icon} alt="lang" className="w-5" />
                <span className="text-xs text-[#605EA3]">{otherLangLabel}</span>
              </button>
            </div>

            {/* CTA */}
            <button
              onClick={handlePrimaryCtaClick}
              className="bg-[#4242CE] hover:bg-indigo-700 text-white text-sm font-medium px-4 py-3 rounded-xl transition-colors duration-200 whitespace-nowrap max-w-fit"
            >
              {t("footerV2.download")}
            </button>
          </div>

          {/* Mobile Right */}
          <div className="flex md:hidden items-center gap-3">
            {/* CTA */}
            <button
              onClick={() => {
                setMenuOpen(false);
                if (onPrimaryCtaClick) onPrimaryCtaClick();
              }}
              className="bg-[#4242CE] text-white text-xs font-semibold px-4 py-3 rounded-xl whitespace-nowrap max-w-fit"
            >
              {t("footerV2.download")}
            </button>

            {/* Hamburger / Close */}
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="flex flex-col justify-center items-center w-8 h-8 gap-1.5"
              aria-label="Toggle menu"
            >
              <span
                className={`block h-0.5 w-6 bg-[#4242CE] transition-all duration-300 ${
                  menuOpen ? "rotate-45 translate-y-2" : ""
                }`}
              />
              <span
                className={`block h-0.5 w-6 bg-[#4242CE] transition-all duration-300 ${
                  menuOpen ? "opacity-0" : ""
                }`}
              />
              <span
                className={`block h-0.5 w-6 bg-[#4242CE] transition-all duration-300 ${
                  menuOpen ? "-rotate-45 -translate-y-2" : ""
                }`}
              />
            </button>
          </div>
        </div>
      </nav>

      {/* Full-screen Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 bg-white flex flex-col md:hidden transition-all duration-300 ${
          menuOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
      >
        {/* Top bar with logo and close */}
        <div className="flex items-center justify-between px-6 py-8 border-b border-[#EBEBEB]">
          <Link to="/" onClick={() => setMenuOpen(false)}>
            <img src={logo} alt="logo" className="w-[50px]" />
          </Link>
          <button
            onClick={() => setMenuOpen(false)}
            className="text-gray-500 hover:text-gray-800 text-2xl font-light"
            aria-label="Close menu"
          >
            ✕
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex flex-col flex-1 overflow-y-auto px-6">
          {/* Primary Nav Links */}
          <div className="border-b border-gray-200 py-6">
            <ul className="flex flex-col gap-6">
              {primaryNavLinks.map((link) => (
                <li key={link.label}>
                  {link.external ? (
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[#4242CE] text-xl font-semibold"
                      onClick={() => setMenuOpen(false)}
                    >
                      {link.label}
                    </a>
                  ) : (
                    <NavLink
                      to={link.to}
                      className={({ isActive }) =>
                        `text-[#4242CE] text-xl font-semibold ${isActive ? activeLinkClassName : ""}`
                      }
                      onClick={() => setMenuOpen(false)}
                    >
                      {link.label}
                    </NavLink>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Secondary Links */}
          <div className="border-b border-gray-200 py-6">
            <ul className="flex flex-col gap-5">
              {secondaryLinks.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className="text-gray-500 text-base"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setMenuOpen(false)}
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Language Selector */}
          <div className="border-b border-gray-200 py-5">
            <button
              onClick={() => {
                handleLanguageChange(currentLang === "en" ? "ar" : "en");
                setMenuOpen(false);
              }}
              className="flex items-center gap-2 text-gray-600 text-base"
            >
              <img src={globe_icon} alt="lang" className="w-5" />
              <span>{t("header.preferredLanguage")}</span>
              <span className="font-bold text-gray-900">
                {currentLang === "en" ? "English" : "العربية"}
              </span>
            </button>
          </div>

          {/* Download App CTA */}
          <div className="py-6">
            <button
              onClick={() => setMenuOpen(false)}
              className="w-full bg-[#4242CE] hover:bg-indigo-700 text-white text-base font-semibold py-4 rounded-2xl transition-colors duration-200"
            >
              {t("footerV2.download")}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
